import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { useGetEmailThreadChatLazyQuery } from '@shared/generated/graphql';
import { MAX_EMAIL_THREAD_CHAT_MESSAGE_IDS } from 'clerk_common/constants/chats';
import { useEffect, useState } from 'react';
import { PageChat } from './pageChat';
import { useChangeCount } from './useChangeCount';

type UseEmailThreadChatInput = {
  hasMessagesFailed?: boolean;
  isLoadingMessages: boolean;
  messageIds: string[];
};

type Lookup = {
  lookupId: number;
  conversationId: string | null;
  hasFailed: boolean;
};

export function useEmailThreadChat({
  hasMessagesFailed = false,
  isLoadingMessages,
  messageIds,
}: UseEmailThreadChatInput): PageChat {
  const { defaultOrgId } = useMeContext();
  const { thisItem } = useEmailThreadContext();
  const emailKey = thisItem?.id;
  const messageIdsKey = [...messageIds]
    .sort()
    .slice(0, MAX_EMAIL_THREAD_CHAT_MESSAGE_IDS)
    .join(',');
  const hasMessages = messageIdsKey.length > 0;
  const visitId = useChangeCount(`${defaultOrgId}|${emailKey}`);
  const lookupId = useChangeCount(
    `${defaultOrgId}|${emailKey}|${messageIdsKey}`
  );
  const [fetchEmailThreadChat] = useGetEmailThreadChatLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [lookup, setLookup] = useState<Lookup | null>(null);

  useEffect(() => {
    if (!emailKey || !defaultOrgId || !hasMessages || isLoadingMessages) {
      return;
    }
    let isCurrent = true;
    fetchEmailThreadChat({
      variables: {
        input: {
          organizationId: defaultOrgId,
          externalMessageIds: messageIdsKey.split(','),
        },
      },
    })
      .then(({ data, error }) => {
        if (!isCurrent) return;
        setLookup({
          lookupId,
          conversationId: error ? null : data?.emailThreadChat?.id ?? null,
          hasFailed: Boolean(error),
        });
      })
      .catch(() => {
        if (isCurrent) {
          setLookup({ lookupId, conversationId: null, hasFailed: true });
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [
    defaultOrgId,
    emailKey,
    fetchEmailThreadChat,
    hasMessages,
    isLoadingMessages,
    lookupId,
    messageIdsKey,
  ]);

  const isLookupDone = lookup?.lookupId === lookupId;
  const hasFailed = hasMessages
    ? isLookupDone && lookup.hasFailed
    : hasMessagesFailed;
  const isResolved = !isLoadingMessages && (!hasMessages || isLookupDone);

  return {
    visitId: emailKey ? visitId : undefined,
    conversationId: hasMessages && isLookupDone ? lookup.conversationId : null,
    isResolved,
    isFinal: isResolved && hasMessages && !hasFailed,
    hasFailed,
  };
}
